import { cloneDeep } from 'lodash-es';
import {getFacetConfig, canonicalFilterKey} from "./facetConfigUtils";
import * as openalexId from "@/openalexId";


const createFilterId = function (key, value, isNegated) {
    key = key.toLowerCase()

    const negateSymbol = (isNegated) ? "!" : ""

    const openAlexUrlBase = "https://openalex.org/"
    if (typeof value === "string" && value.indexOf(openAlexUrlBase) === 0) {
        value = value.replace("https://openalex.org/", "").toLowerCase()
    }
    return `${key}:${negateSymbol}${value}`
}


const splitFilterString = function (str) {
    // Split by comma, but respect double-quoted strings
    // e.g., 'foo:bar,baz:"hello, world"' -> ['foo:bar', 'baz:"hello, world"']
    const parts = []
    let current = ""
    let inQuotes = false

    for (const char of str) {
        if (char === '"') {
            inQuotes = !inQuotes
            current += char
        } else if (char === ',' && !inQuotes) {
            if (current) {
                parts.push(current)
            }
            current = ""
        } else {
            current += char
        }
    }

    if (current) {
        parts.push(current)
    }

    return parts
}

const filtersFromUrlStr = function (entityType, str) {
    if (!str) return []
    if (str.indexOf(":") === -1) return []

    const filterStrings = splitFilterString(str)
    const filters = []
    filterStrings.forEach(filterString => {
        const regex = /(?<!http|https):/
        const [key, ...rest] = filterString.split(regex);
        const valuesStr = rest.join(':');
        const value = valuesStr.replace(/^!/, "");
        const isNegated = valuesStr[0] === "!"
        const myFilter = createSimpleFilter(
            entityType,
            key,
            value,
            isNegated,
        )
        filters.push(myFilter)
    })
    return filters
}


const getMatchModeFromSelectFilterValue = function (valueStr) {
    return (valueStr?.indexOf(",") > -1) ? "all" : "any"
}


const optionsToString = function (options) {
    return options.join("|")
}


const optionsFromString = function (str) {
    if (typeof str !== "string") { return null; }
    const strWithoutNegation = str.replace(/^!/, "")
    const regex = /[+|]/
    return strWithoutNegation.split(regex).map(option => {
        // Collection IDs (`col_<base58>`) are case-sensitive and are NOT
        // OpenAlex entities — they can appear as the value of a regular
        // entity-ID field (cross-type filter, oxjob #273). Preserve their case;
        // lowercase everything else as before.
        return openalexId.isCollectionId(option) ? option : option.toLowerCase()
    })
}


const deleteOptionFromFilterValue = function (valueStr, optionToDelete) {
    const oldOptions = optionsFromString(valueStr)
    const newOptions = oldOptions.filter(oldOption => {
        return oldOption !== optionToDelete
    })
    return optionsToString(newOptions)
}


const addOptionToFilterValue = function (valueStr, optionToAdd) {
    // const matchMode = getMatchModeFromSelectFilterValue(valueStr)
    const oldOptions = optionsFromString(valueStr)
    const newOptions = [...oldOptions, optionToAdd]
    return optionsToString(newOptions)
}


const toggleNegation = function (option) {
    return option[0] === "!" ?
        option.substr(1) :
        "!" + option

}


const negateOption = function (option) {
    return option[0] === "!" ?
        option :
        "!" + option
}


const removeNegationFromOption = function (option) {
    return option[0] === "!" ?
        option.substr(1) :
        option
}
const setOptionIsNegated = function(option, isNegated) {
    return isNegated ?
        negateOption(option) :
        removeNegationFromOption(option)
}

// alias because i'm refactoring
const setStringIsNegated = function(string, isNegated) {
    return isNegated ?
        negateOption(string) :
        removeNegationFromOption(string)
}


const toggleOptionIsNegated = function (valueStr, optionToToggleNegation) {
    const oldOptions = optionsFromString(valueStr)
    const newOptions = oldOptions.map(oldOption => {
        return oldOption === optionToToggleNegation ?
            toggleNegation(oldOption) :
            oldOption

    })
    return optionsToString(newOptions)
}


const makeSelectFilterValue = function (items, matchMode) {
    const sep = {
        any: "|",
        all: "+",
        none: "|"
    }
    const prepend = (matchMode === "none") ? "!" : ""
    return prepend + items.join(sep[matchMode])
}


const filtersAreEqual = function (f1, f2) {
    const sameKey = f1.key === f2.key
    const sameValue = f1.value === f2.value
    const sameNegation = f1.isNegated === f2.isNegated
    return sameKey && sameValue && sameNegation
}


const filtersAsUrlStr = function (filters) {
    const filtersAsStrings = filters.map((f => f.asStr))
    const dedupedFilterStrings = new Set([...filtersAsStrings])
    return [...dedupedFilterStrings].join(",")
}


const filtersFromFiltersApiResponse = function (entityType, apiFacets) {
    let ret = []
    apiFacets
        .filter(f => f.key !== "search")
        .forEach(facet => {
            facet.values.forEach(valueObj => {
                const isNegated = facet.is_negated
                ret.push(createDisplayFilter(
                    entityType,
                    facet.key,
                    valueObj.value,
                    isNegated,
                    valueObj.display_name,
                    valueObj.count,
                ))
            })
        })
    ret = ret.filter(f => f.key !== "search")
    return ret
}


const createFilterValue = function (rawValue, filterType) {
    if (filterType === "search") {
        return rawValue
    }
    if (typeof rawValue === "string") {
        // Strip surrounding quotes (used by API for values with spaces)
        if (rawValue.startsWith('"') && rawValue.endsWith('"')) {
            rawValue = rawValue.slice(1, -1)
        }
        // rawValue = rawValue.replace("https://openalex.org/", "")
        rawValue = openalexId.getShortId(rawValue) || rawValue
        // rawValue = rawValue.replace("unknown", null)
    }
    if (filterType === "boolean") {
        if (rawValue === undefined) {
            rawValue = true //boolean filters default to true
        }
        if (rawValue == "true") rawValue = true
        if (rawValue == "false") rawValue = false
    }
    return rawValue
}


const createSimpleFilter = function (entityType, key, value, isNegated) {
    // console.log("createSimpleFilter", key, value, isNegated)
    if (!key) {
        throw Error(
            `OpenAlex: createSimpleFilter(): no key provided.`
        )
    }
    key = canonicalFilterKey(entityType, key)
    const facetConfig = getFacetConfig(entityType, key)
    if (!facetConfig) {
        // Unknown filter key for this entity type (e.g. `is_oa` on /works —
        // the OX API accepts it as a shorthand for `open_access.is_oa` but
        // the GUI's facetConfigs only registers `is_oa` for /sources and
        // /locations). Returning a passthrough filter lets URLs containing
        // such filters round-trip to the API: filtersAsUrlStr serializes by
        // `.asStr` and the API validates server-side. Chip-rendering surfaces
        // bail when they look up `getFacetConfig(entityType, key)` and get
        // null — they were already null-safe.
        //
        // Previous behavior was to throw; that killed any reactive computed
        // (e.g. NoviceFilterChips' `allUrlFilters`) that touched the URL
        // during render, which is how oxjob #228 QA-051 surfaced:
        // `?filter=collection:X,is_oa:true` rendered an empty SERP with no
        // chips, no facets, no error — because the throw bubbled into Vue's
        // render machinery and bailed the whole subtree.
        console.warn(
            `OpenAlex: createSimpleFilter(): no facetConfig found for '${entityType}' filter "${key}" — passing through to API.`
        )
        const passValue = (typeof value === "string") ? value : value
        const passNeg = !!isNegated
        const passStr = key + ":" + (passNeg ? "!" : "") + passValue
        return {
            key,
            displayName: key,
            entityToFilter: entityType,
            type: "unknown",
            value: passValue,
            asStr: passStr,
            kv: createFilterId(key, passValue),
            isNegated: passNeg,
            isNullValue: (passValue === null),
        }
    }
    // quoteValue filters hold plain values: strip the API quotes like any non-search value.
    const myValue = createFilterValue(value, facetConfig.quoteValue ? "value" : facetConfig.type)
    if (!myValue) isNegated = true

    const nullValues = ["unknown", "null"]
    const apiValue = (nullValues.includes(myValue)) ? null : myValue

    const negationSymbol = (isNegated && !!myValue) ?
        "!" :
        ""
    // Wrap space-containing values in quotes for API compatibility (e.g. an
    // institution display-name select filter). EXCEPT `.search`-type filters:
    // their value is a raw API query string that may already contain quotes,
    // proximity (`~N`), or Boolean groups (`|`/`+`). Wrapping those would
    // double-quote the query and corrupt it (regression c8e689f7 → #191.5).
    // `quoteValue` (oxjob #1526): a typed exact-value filter (institution region/city)
    // whose values can contain spaces ("New York") but never query syntax.
    const isSearchFilter = facetConfig.type === "search" && !facetConfig.quoteValue
    const quotedValue = (typeof apiValue === "string" && apiValue.includes(" ") && !isSearchFilter) ? `"${apiValue}"` : apiValue
    const asStr = facetConfig.key + ":" + negationSymbol + quotedValue

    return {
        ...facetConfig,
        // key,
        value: myValue,
        asStr,
        kv: createFilterId(key, apiValue),
        // isEntity: entityKeys.includes(key),
        isNegated: !!isNegated,
        isNullValue: (apiValue === null),
    }
}


const copySimpleFilter = function (filter, overwriteWith) {

    return createSimpleFilter(
        overwriteWith?.entityToFilter ?? filter.entityToFilter,
        overwriteWith?.key ?? filter.key,
        overwriteWith?.value ?? filter.value,
        overwriteWith?.isNegated ?? filter.isNegated
    )
}


const convertYearRangeToPrettyWords = function (yearRange) {
    if (yearRange[0] === yearRange[1]) {
        return yearRange[0]
    } else if (!yearRange[0]) {
        // return "Before " + (Number(yearRange[1]) + 1)
        return "Before or in " + (Number(yearRange[1]))
    } else if (!yearRange[1]) {
        return yearRange[0] + " and later"
    } else {
        return yearRange.join("-")
    }
}


const convertRangeToPrettyWords = function (range) {
    if (range[0] === range[1]) {
        return range[0] + " exactly"
    } else if (!range[0]) {
        // return (Number(range[1]).toLocaleString() ) + " or fewer"
        return "At most " + (Number(range[1]).toLocaleString())
    } else if (!range[1]) {
        return Number(range[0]).toLocaleString() + " or more"
    } else {
        return range.join("-")
    }
}


const createDisplayFilter = function (entityType, key, value, isNegated, displayValue, count, countScaled) {
    const simpleFilter = createSimpleFilter(entityType, key, value, isNegated)
    if (simpleFilter.valuesToShow === 'range' && /\d*-\d*/.test(value)) {
        if (key === "publication_year") {
            displayValue = convertYearRangeToPrettyWords(value.split("-"))
        } else {
            displayValue = convertRangeToPrettyWords(value.split("-"))
        }

    }
    if (simpleFilter.displayNullAs && ["unknown", "null", null].includes(value)) {
        displayValue = simpleFilter.displayNullAs

    }

    return {
        ...simpleFilter,
        displayValue,
        count,
        countScaled,
    }
}


const displayYearRange = function (range) {
    range = range.map(r => {
        return (r === null || r === Infinity) ? "" : r
    })

    const currentYear = new Date().getFullYear()
    if (range[0] === "" && range[1] === "") return null
    else if (range[0] === range[1]) return range[0]
    else if (range[0] === "") return "through " + range[1]
    else if (range[1] === "") {
        if (range[0] == currentYear) {
            return currentYear
        } else {
            return "since " + range[0]
        }
    } else return range.join("-")
}


// Decide the new flat filter list when an entity value-picker applies its
// selection, given how the picker is being used. This is the collaboration-AND
// fix (Krista @ Concordia, 2026-06): the same key can legitimately appear as
// several separate AND'd clauses (e.g. "institution is McGill" AND "institution
// is Concordia"), so a second institution must be able to AND rather than always
// collapse into one pipe-OR "is any of" clause.
//
//  - filterIndex != null  → advanced row edit: replace ONLY the clause at that
//    index, leaving every other clause (incl. other same-key AND clauses) alone.
//    An empty selection drops just that row.
//  - appendNew            → "Add filter": add a NEW clause, preserving existing
//    same-key clauses (so the second institution ANDs with the first).
//  - neither              → basic chip (one chip per facet): collapse all
//    same-key clauses into this single selection (OR) — existing basic behavior.
//
// `currentFilters` is the array from filtersFromUrlStr; returns a new array.
const applyEntitySelection = function ({
    entityType,
    filterKey,
    currentFilters,
    selectedEntityIds = [],
    selectedCollectionId = null,
    filterIndex = null,
    appendNew = false,
}) {
    // The clause this selection represents: a single collection ref, a pipe-OR'd
    // set of entity IDs, or null when nothing is selected (a "clear").
    let newClause = null
    if (selectedCollectionId) {
        newClause = createSimpleFilter(entityType, filterKey, selectedCollectionId)
    } else if (selectedEntityIds.length) {
        newClause = createSimpleFilter(entityType, filterKey, selectedEntityIds.join("|"))
    }

    if (filterIndex != null) {
        return currentFilters
            .map((f, i) => (i === filterIndex ? newClause : f))
            .filter(Boolean)
    }
    if (appendNew) {
        return newClause ? [...currentFilters, newClause] : [...currentFilters]
    }
    const others = currentFilters.filter(x => x.key !== filterKey)
    if (newClause) others.push(newClause)
    return others
}


// The facet "More..." dialog (GroupBy.vue) owns one field's plain values in
// un-negated clauses, OR'd. A collection or a negated clause keeps its own
// clause and the picks get another (same-key clauses AND): the API refuses a
// collection in a pipe list, `topics.id:col_x|t1` (oxjob #1942).
const facetDialogSelection = function (currentFilters, filterKey) {
    return currentFilters
        .filter(f => f.key === filterKey && !f.isNegated)
        .flatMap(f => optionsFromString(f.value))
        .filter(v => !openalexId.isCollectionId(v))
}

const applyFacetDialogSelection = function ({
    entityType,
    filterKey,
    currentFilters,
    selectedValues = [],
}) {
    // Each collection stays in place as its own clause (also splits an old mixed clause).
    const kept = currentFilters.flatMap(f => (f.key !== filterKey || f.isNegated) ? [f] :
        optionsFromString(f.value)
            .filter(openalexId.isCollectionId)
            .map(c => createSimpleFilter(entityType, filterKey, c)))
    const picks = selectedValues.filter(v => !openalexId.isCollectionId(v))
    if (picks.length) kept.push(createSimpleFilter(entityType, filterKey, optionsToString(picks)))
    return kept
}


const sortedFilters = function (filters, sortByValue) {
    const ret = cloneDeep(filters)

    if (sortByValue) {
        ret.sort((a, b) => {
            return (a.value > b.value) ? -1 : 1
        })
    } else {
        ret.sort((a, b) => {
            return b.count - a.count
        })
    }
    return ret
}


export {
    filtersAsUrlStr,
    filtersFromUrlStr,
    splitFilterString,
    filtersAreEqual,
    filtersFromFiltersApiResponse,
    createSimpleFilter,
    applyEntitySelection,
    facetDialogSelection,
    applyFacetDialogSelection,
    copySimpleFilter,
    createDisplayFilter,
    createFilterId,
    displayYearRange,
    deleteOptionFromFilterValue,
    addOptionToFilterValue,
    toggleOptionIsNegated,
    setOptionIsNegated,
    setStringIsNegated,
    optionsToString,
    optionsFromString,
    sortedFilters,
    getMatchModeFromSelectFilterValue,
    makeSelectFilterValue,
}